import { Button, FormControl, MenuItem, Select, Stack } from '@mui/material'
import { useTranslation } from 'react-i18next'
import type { SelectChangeEvent } from '@mui/material/Select'
import { useMutation } from '@tanstack/react-query'
import { useSnackbar } from 'notistack'
import * as yup from 'yup'
import { flatMap } from 'lodash'
import { useAPI } from '../../api'
import { InvoicePayload, InvoiceRow, SheetError } from '../Invoice/types'
import { PrintTemplateDetail } from '../OCRImport/types'

interface DataExportButtonProps {
  data: InvoiceRow[] | PrintTemplateDetail[] | undefined
  filter: any
  fileName: string
  format?: string
  handleFormatChange: (event: SelectChangeEvent<string>) => void
  sheetSchema?: yup.AnySchema
  setErrors?: (errors: SheetError[]) => void
}

export default function DataExportButton({
  data,
  filter,
  fileName,
  format = 'En',
  handleFormatChange,
  sheetSchema,
  setErrors,
}: DataExportButtonProps) {
  const { t } = useTranslation()
  const api = useAPI()
  const { enqueueSnackbar } = useSnackbar()

  const exportMutation = useMutation({
    mutationFn: (payload: InvoicePayload[]) =>
      api.toFile('invoice', payload, false, fileName || 'DefaultTemplateName'),
    onSuccess: () => {
      enqueueSnackbar(t('messages.success', { action: t('actions.export') }), {
        variant: 'success',
      })
    },
  })

  const exportHandler = async () => {
    if (!data) return

    try {
      let exportData = data
      if (sheetSchema) {
        await sheetSchema.validate(data, { abortEarly: false })
        exportData = sheetSchema.cast(data)
      }

      const payloadWithFilterData = [
        ...exportData,
        {
          templateName: fileName || 'DefaultTemplateName',
          format: format,
        },
        filter,
      ]
      await exportMutation.mutateAsync(payloadWithFilterData as InvoicePayload[])
    } catch (error) {
      if (error instanceof yup.ValidationError && setErrors) {
        const errors: SheetError[] = flatMap(error.inner, (e) => {
          if (e.type === 'rowError') {
            const row = e.path!.split(',').map((i) => parseInt(i))
            return row.map((r) => ({
              row: r,
              message: e.message,
            }))
          } else {
            const [rowIndex, column] = e.path!.split('.')
            const row = parseInt(rowIndex.replace(/\D/g, ''))
            return {
              row,
              column,
              message: e.message,
            }
          }
        })
        setErrors(errors)
      } else {
        console.log(error)
      }
    }
  }

  return (
    <Stack flexDirection="row-reverse" gap={1}>
      <Button
        variant="contained"
        disabled={exportMutation.isPending}
        onClick={exportHandler}
      >
        {t('actions.export')}
      </Button>
      <FormControl>
        <Select
          id="format-select"
          value={format}
          onChange={handleFormatChange}
        >
          <MenuItem value={'En'}>En</MenuItem>
          <MenuItem value={'JP'}>JP</MenuItem>
        </Select>
      </FormControl>
    </Stack>
  )
}
